import React, { useState } from 'react';
import { Button, message } from 'antd';
import { Document, Packer, Paragraph, TextRun, Table, TableCell, TableRow, AlignmentType, WidthType, BorderStyle, HeadingLevel, ImageRun, Footer, Header, PageNumber } from 'docx';

// Export the document generation function
export const createSOWDocument = async (data = {}) => {
  // Default data structure
  const defaultData = {
    title: "GSC-Business Support-Reporting and business enabling services-Configuration of customized reports-SOW-V1.0",
    version: "V1.0",
    signedOffDate: "25-11-2025",
    signedBy: "Kalpesh bhatia",
    role: "Senior Manager",
    ...data
  };

  let logoBase64 = null;
  
  try {
    // Fetch and convert DHL logo to base64
    const logoResponse = await fetch('https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/DHL_Logo.svg/2560px-DHL_Logo.svg.png');
    const logoBlob = await logoResponse.blob();
    logoBase64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(logoBlob);
    });
  } catch (error) {
    console.error('Error loading logo:', error);
  }

  // Create header with DHL logo
  const header = logoBase64 ? new Header({
    children: [
      new Paragraph({
        children: [
          new ImageRun({
            data: Uint8Array.from(atob(logoBase64), c => c.charCodeAt(0)),
            transformation: {
              width: 180,
              height: 54
            }
          })
        ],
        spacing: { after: 200 },
        border: {
          bottom: {
            color: "FFCC00",
            space: 1,
            style: BorderStyle.SINGLE,
            size: 24
          }
        }
      })
    ]
  }) : new Header({
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text: "DHL",
            bold: true,
            size: 24
          })
        ],
        spacing: { after: 200 }
      })
    ]
  });

  // Create footer with page numbers
  const footer = new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            children: ["Page ", PageNumber.CURRENT, " of ", PageNumber.TOTAL_PAGES],
            font: "Delivery",
            size: 10 * 2
          })
        ]
      })
    ]
  });

  // Create document with proper styling
  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: {
            top: 1080,
            right: 1134,
            bottom: 1080,
            left: 1134
          },
          pageNumbers: {
            start: 1,
            formatType: "decimal"
          }
        }
      },
      headers: {
        default: header
      },
      footers: {
        default: footer
      },
      children: [
        // Additional spacing after header
        new Paragraph({
          text: "",
          spacing: { after: 400 }
        }),

        // Cover Page Header
        new Paragraph({
          children: [
            new TextRun({
              text: "GLOBAL SOP-STATEMENT OF WORK SOP TEMPLATE",
              font: "Delivery",
              size: 26 * 2,
              bold: true
            })
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
          border: {
            bottom: {
              color: "7F7F7F",
              space: 1,
              style: BorderStyle.SINGLE,
              size: 12
            }
          }
        }),

        // Title
        new Paragraph({
          children: [
            new TextRun({
              text: defaultData.title,
              font: "Delivery",
              size: 15 * 2,
              bold: true
            })
          ],
          alignment: AlignmentType.CENTER,
          spacing: { before: 120, after: 200 }
        }),

        // Page Break
        new Paragraph({
          text: "",
          pageBreakBefore: true
        }),

        // Contents Heading
        new Paragraph({
          text: "Contents",
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 200 }
        }),

        // Table of Contents
        createTOCEntry("1", "SOP INTRODUCTION", "3"),
        createTOCEntry("1.1", "Statement of Work", "3", true),
        createTOCEntry("1.2", "SIPOC", "3", true),
        createTOCEntry("1.3", "Relevant Tools", "4", true),
        createTOCEntry("1.4", "Version Control", "4", true),
        createTOCEntry("2", "INPUT FILES", "5"),
        createTOCEntry("3", "REFERENCE DOCUMENTS", "6"),
        createTOCEntry("4", "BUSINESS EXCEPTIONS", "7"),

        // Page Break
        new Paragraph({
          text: "",
          pageBreakBefore: true
        }),

        // Section 1 - SOP Introduction
        new Paragraph({
          text: "1     SOP INTRODUCTION",
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 480, after: 240 }
        }),

        // Section 1.1
        new Paragraph({
          text: "1.1    STATEMENT OF WORK",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 240, after: 120 }
        }),

        new Paragraph({
          children: [
            new TextRun({
              text: "We provide support in creating customized reports for BPs, which are scheduled and triggered as per the customer's specific requirements. We arrange calls with BPs to understand Report requirements for each request and setup one-time customized reports using existing DataMart's or modifying standard reports to cater to customer / operational requirements.",
              font: "Delivery",
              size: 11 * 2
            })
          ],
          spacing: { after: 200 }
        }),

        // Section 1.2 - SIPOC
        new Paragraph({
          text: "1.2    SIPOC",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 240, after: 120 }
        }),

        createSIPOCTable(),

        // Section 1.3 - Relevant Tools
        new Paragraph({
          text: "1.3    RELEVANT TOOLS",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 480, after: 120 }
        }),

        createRelevantToolsTable(),

        // Section 1.4 - Version Control
        new Paragraph({
          text: "1.4    VERSION CONTROL",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 240, after: 120 }
        }),

        createVersionControlTable(defaultData),

        // Page Break
        new Paragraph({
          text: "",
          pageBreakBefore: true
        }),

        // Section 2 - Input Files
        new Paragraph({
          text: "2      INPUT FILES",
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 480, after: 240 }
        }),

        createInputFilesTable(),

        // Section 3 - Reference Documents
        new Paragraph({
          text: "3      REFERENCE DOCUMENTS",
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 480, after: 240 }
        }),

        createReferenceDocumentsTable(),

        // Section 4 - Business Exceptions
        new Paragraph({
          text: "4      BUSINESS EXCEPTIONS",
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 480, after: 240 }
        }),

        new Paragraph({
          text: "Not Applicable",
          font: "Delivery",
          spacing: { after: 200 }
        })
      ]
    }]
  });

  return doc;
};

// Helper function to create TOC entries with table for alignment
const createTOCEntry = (number, text, page, isSubItem = false) => {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.NONE },
      bottom: { style: BorderStyle.NONE },
      left: { style: BorderStyle.NONE },
      right: { style: BorderStyle.NONE },
      insideHorizontal: { style: BorderStyle.NONE },
      insideVertical: { style: BorderStyle.NONE }
    },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${number}       ${text}`,
                    font: "Delivery",
                    size: isSubItem ? 11 * 2 : 10 * 2
                  })
                ],
                spacing: { before: 60, after: 60 }
              })
            ],
            width: { size: 85, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE }
            },
            margins: {
              left: isSubItem ? 850 : 435
            }
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: page,
                    font: "Delivery",
                    size: isSubItem ? 11 * 2 : 10 * 2
                  })
                ],
                alignment: AlignmentType.RIGHT,
                spacing: { before: 60, after: 60 }
              })
            ],
            width: { size: 15, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE }
            }
          })
        ]
      })
    ]
  });
};

// SIPOC Table
const createSIPOCTable = () => {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      // Header Row
      new TableRow({
        children: [
          createTableCell("Supplier", true),
          createTableCell("Input", true),
          createTableCell("Process", true, "#FFE599"),
          createTableCell("Output", true),
          createTableCell("Customer", true)
        ]
      }),
      // Data Rows
      new TableRow({
        children: [
          createTableCell("DGFF"),
          createTableCell("Business Requirement Specification (BRS)"),
          createTableCell("Start", false, "#FFE599"),
          createTableCell("Customer Reports"),
          createTableCell("DGFF")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell(""),
          createTableCell("Extract data from DCR"),
          createTableCell(""),
          createTableCell("")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell(""),
          createTableCell("Data Mapping"),
          createTableCell(""),
          createTableCell("")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell(""),
          createTableCell("Designing"),
          createTableCell(""),
          createTableCell("")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell(""),
          createTableCell("Testing"),
          createTableCell(""),
          createTableCell("")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell(""),
          createTableCell("Handover"),
          createTableCell(""),
          createTableCell("")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell(""),
          createTableCell("Maintenance and support"),
          createTableCell(""),
          createTableCell("")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell(""),
          createTableCell("End", false, "#FFE599"),
          createTableCell(""),
          createTableCell("")
        ]
      })
    ]
  });
};

// Relevant Tools Table
const createRelevantToolsTable = () => {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        children: [
          createTableCell("Tool Name", true, "#FFE593"),
          createTableCell("Relevance for the SOP", true, "#FFE593")
        ]
      }),
      new TableRow({
        children: [
          createTableCell("Request Forms"),
          createTableCell("Requirement Gathering")
        ]
      }),
      new TableRow({
        children: [
          createTableCell("Reporting Template"),
          createTableCell("Reference for report creation")
        ]
      }),
      new TableRow({
        children: [
          createTableCell("Teams"),
          createTableCell("Connecting with stake holders to gather requirements")
        ]
      }),
      new TableRow({
        children: [
          createTableCell("Outlook"),
          createTableCell("Email follow up")
        ]
      }),
      new TableRow({
        children: [
          createTableCell("IBM Cognos"),
          createTableCell("Report Development")
        ]
      })
    ]
  });
};

// Version Control Table
const createVersionControlTable = (data) => {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        children: [
          createTableCell("Version #", true, "#FFE593"),
          createTableCell("Signed off Date", true, "#FFE593"),
          createTableCell("Signed By", true, "#FFE593"),
          createTableCell("Role", true, "#FFE593"),
          createTableCell("Comments", true, "#FFE593")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(data.version),
          createTableCell(data.signedOffDate),
          createTableCell(data.signedBy),
          createTableCell(data.role),
          createTableCell("NA")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell(""),
          createTableCell(""),
          createTableCell(""),
          createTableCell("")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell(""),
          createTableCell(""),
          createTableCell(""),
          createTableCell("")
        ]
      })
    ]
  });
};

// Input Files Table with clickable link
const createInputFilesTable = () => {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        children: [
          createTableCell("Input File Names", true, "#FFE593"),
          createTableCell("Input file Path & Template", true, "#FFE593")
        ]
      }),
      new TableRow({
        children: [
          createTableCell("Reporting Template"),
          createTableCell("Excel file attached in ServiceNow request")
        ]
      }),
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Request Template",
                    font: "Delivery",
                    size: 10 * 2
                  })
                ],
                alignment: AlignmentType.LEFT
              })
            ],
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              right: { style: BorderStyle.SINGLE, size: 1, color: "000000" }
            }
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Link is available in Request Form\n",
                    font: "Delivery",
                    size: 10 * 2
                  })
                ]
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "https://gsd.dhl.com/forms/3063",
                    font: "Delivery",
                    size: 10 * 2,
                    color: "0563C1",
                    underline: {},
                    style: "Hyperlink"
                  })
                ]
              })
            ],
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              right: { style: BorderStyle.SINGLE, size: 1, color: "000000" }
            }
          })
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell("")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell("")
        ]
      })
    ]
  });
};

// Reference Documents Table
const createReferenceDocumentsTable = () => {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        children: [
          createTableCell("Source Document Type", true, "#FFE593"),
          createTableCell("Source document title", true, "#FFE593"),
          createTableCell("created by", true, "#FFE593")
        ]
      }),
      new TableRow({
        children: [
          createTableCell("Word"),
          createTableCell("DCR_Business Definitions"),
          createTableCell("Audrey Kuster")
        ]
      }),
      new TableRow({
        children: [
          createTableCell("Excel"),
          createTableCell("DCR_Timestamps"),
          createTableCell("Audrey Kuster")
        ]
      }),
      new TableRow({
        children: [
          createTableCell(""),
          createTableCell(""),
          createTableCell("")
        ]
      })
    ]
  });
};

// Helper function to create table cells
const createTableCell = (text, isHeader = false, backgroundColor = null) => {
  return new TableCell({
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text: text,
            font: "Delivery",
            size: 10 * 2,
            bold: isHeader
          })
        ],
        alignment: isHeader ? AlignmentType.CENTER : AlignmentType.LEFT
      })
    ],
    shading: backgroundColor ? {
      fill: backgroundColor
    } : undefined,
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      right: { style: BorderStyle.SINGLE, size: 1, color: "000000" }
    }
  });
};

// React Component
const SOWDocumentGenerator = () => {
  console.log("log generate SOW Template")
  const [loading, setLoading] = useState(false);

  const handleGenerateDocument = async () => {
    try {
      setLoading(true);
      message.loading({ content: 'Generating document...', key: 'generate' });

      const doc = await createSOWDocument();
      
      message.success({ content: 'Document template generated successfully!', key: 'generate', duration: 2 });
      
      // Return the document for use in Collabora
      return doc;
    } catch (error) {
      console.error('Error generating document:', error);
      message.error({ content: 'Failed to generate document', key: 'generate', duration: 2 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ 
        background: '#fff', 
        padding: '40px', 
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ marginBottom: '24px', color: '#1890ff' }}>
          SOW Document Generator
        </h2>
        
        <p style={{ marginBottom: '24px', color: '#666' }}>
          Generate a Statement of Work document template in DOCX format compatible with Collabora Online.
        </p>

        <Button 
          type="primary" 
          size="large"
          loading={loading}
          onClick={handleGenerateDocument}
          style={{ width: '100%' }}
        >
          {loading ? 'Generating...' : 'Generate SOW Document Template'}
        </Button>

        <div style={{ 
          marginTop: '24px', 
          padding: '16px', 
          background: '#f0f2f5', 
          borderRadius: '4px',
          fontSize: '12px',
          color: '#666'
        }}>
          <strong>Note:</strong> The generated document will include:
          <ul style={{ marginTop: '8px', marginBottom: '0' }}>
            <li>DHL logo on cover page</li>
            <li>Page numbers on all pages</li>
            <li>Table of contents</li>
            <li>SIPOC process table</li>
            <li>Relevant tools documentation</li>
            <li>Version control information</li>
            <li>Input files with clickable link</li>
            <li>Reference documents</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SOWDocumentGenerator;